import styled from '@emotion/styled';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { useMediaQuery } from '../../../utils/hooks/useMediaQuery';
import { breakpointQueries, breakpoints, colors, spacing } from '../../../utils/styleguide';
import { MAX_WIDTH } from '../../component-library/layout/12ColGridLayout';
import NavigationLinks from '../../component-library/navigation/Footer/NavigationLinks';
import SocialMediaInfo from '../../component-library/navigation/Footer/SocialMediaInfo';
import Image from '../Image';
import {
  FooterBarServerProps,
  processAssets,
  processImageLinks,
  processLinkGroups,
  processLinks,
} from '../util/navigation';

const Wrapper = styled.div`
  box-sizing: border-box;
  max-width: 100%;
  padding: 0 ${spacing[4]}px;
  color: ${colors.white};
  ${breakpoints.tablet} {
    padding: 0 ${spacing[5]}px;
  }
  ${breakpoints.desktop} {
    padding: 0 ${spacing[9]}px;
  }
  ${breakpoints.xl} {
    padding: 0 ${spacing[11]}px;
    max-width: ${MAX_WIDTH}px;
    margin: 0 auto;
  }
`;

const GoHomeLinkWrapper = styled.div`
  pointer-events: auto;
  width: auto;
  margin: ${spacing[7]}px 0 0;
  ${breakpoints.tablet} {
    margin: ${spacing[7]}px 0;
  }
`;

const GoHomeLink = styled.a`
  pointer-events: auto;
  display: flex;
  text-decoration: none;
  align-items: center;
`;

function parseFooterFields(footer: FooterBarServerProps) {
  return {
    imageLandscape: footer?.imageLandscape,
    imagePortrait: footer?.imagePortrait,
    linkGroups: processLinkGroups(footer?.linkGroups) ?? [],
    storeGroups: processLinkGroups(footer?.storeGroups) ?? [],
    socialMedia: processImageLinks(footer?.socialMedia) ?? [],
    paymentProviders: processAssets(footer?.paymentProviders) ?? [],
    bottomLinks: processLinks(footer?.bottomLinks ?? []),
    disclaimer: footer?.disclaimer,
    copyright: footer?.copyright,
  };
}

type Props = {
  footerBar: FooterBarServerProps;
};

const ContentfulFooter = ({ footerBar }: Props) => {
  const [isDesktop] = useMediaQuery(breakpointQueries.desktop);

  const {
    imageLandscape,
    imagePortrait,
    linkGroups,
    storeGroups,
    socialMedia,
    paymentProviders,
    bottomLinks,
    disclaimer,
    copyright,
  } = useMemo(() => parseFooterFields(footerBar), [footerBar]);

  return (
    <Wrapper>
      <GoHomeLinkWrapper>
        <Link href="/" passHref>
          <GoHomeLink aria-label="go-home">
            {isDesktop ? <Image asset={imageLandscape} /> : <Image asset={imagePortrait} />}
          </GoHomeLink>
        </Link>
      </GoHomeLinkWrapper>
      <NavigationLinks textLinks={linkGroups} imageLinks={storeGroups} />
      <SocialMediaInfo
        socialMedia={socialMedia}
        paymentProviders={paymentProviders}
        policyLinks={bottomLinks}
        disclaimer={disclaimer}
        copyright={copyright}
      />
    </Wrapper>
  );
};

export default ContentfulFooter;
